import { AccountInfo, PublicClientApplication } from '@azure/msal-browser';
import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';

import LoaderComponent from '../../components/LoaderComponent';
import { Configuration } from '../../config';
import { cleanCSRFToken } from '../../api/csrf';
import { doLogout as serverLogout } from '../../api/bin';
import { REFRESH_BEFORE_EXP_IN_MINS } from '../../utils/constants';
import { SESSION_INITIAL_STATE, sessionReducer } from './sessionReducer';
import {
  loginRedirectRequest,
  loginRedirectRequestMFA,
  msalConfig,
} from './utils/authConfig';
import { useConfig } from '../ConfigProvider';
import WarningDialog from './../../components/WarningDialog/WarningDialog';

export const SessionContext = React.createContext(SESSION_INITIAL_STATE);
export const useSession = () => useContext(SessionContext);

const {
  config: { CLIENT_ID, AUTHORITY },
} = new Configuration();

const SessionProvider = ({ children }: any) => {
  const [state, dispatch] = useReducer(sessionReducer, SESSION_INITIAL_STATE);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [publicClient] = useState(new PublicClientApplication(msalConfig));
  const [openPrompt, setOpenPrompt] = useState(false);
  const [event, setEvent] = useState("");
  
  useEffect(() => {
    sessionStorage.setItem("showPrompt",'false');
  }, []);

  const handleNo = () => {
    setOpenPrompt(false);
  };

  const handleYes = () => {
    setOpenPrompt(false);
    sessionStorage.setItem("showPrompt",'false');
    ((event === 'logout') ? doLogout() : doChangeMFAMethod())
  };

  const doLogout = useCallback(() => {
    var showPrompt = (sessionStorage.getItem("showPrompt") === 'true');
    setOpenPrompt(showPrompt);
    setEvent('logout');
    if(!showPrompt){
    serverLogout('Logout')
      .then((res) => {
        logout();
      })
      .catch((err) => {
        logout();
      });
    }
  }, [publicClient]);

  const logout = () => {
    try {
      sessionStorage.clear();
      publicClient.logoutRedirect();
    } catch (err) {
      console.error(err);
    }
  };

  const doChangeMFAMethod = useCallback(async () => {
    try {
      var showPrompt = (sessionStorage.getItem("showPrompt") === 'true');
      setOpenPrompt(showPrompt);
      setEvent('MFA');
      if(!showPrompt){
        await publicClient.loginRedirect(loginRedirectRequestMFA);
      }
    } catch (err) {
      console.error(err);
    }
  }, [publicClient]);

  const dispatchEvent = useCallback(
    (type: string, userInfo?: any, access_Token?: string) => {
      dispatch({
        type: type,
        payLoad: {
          firstName: userInfo?.given_name ?? '',
          lastName: userInfo?.family_name ?? '',
          email: userInfo?.email ?? '',
          accessToken: access_Token,
          logout: doLogout,
          changeMFAMethod: doChangeMFAMethod,
        },
      });
    },
    [doLogout, doChangeMFAMethod],
  );

  useLogin(publicClient, dispatchEvent, setIsLoggedIn);

  return (
    <>
      {isLoggedIn ? (
        <>
          <SessionContext.Provider value={state}>
            {children}
          </SessionContext.Provider>
        </>
      ) : (
        <LoaderComponent height={'100vh'} />
      )}
      <WarningDialog
        showPrompt={openPrompt}
        title={'Discard'}
        contentText={'Do you want to discard unsaved changes?'}
        onNo={handleNo}
        onYes={handleYes}
      />
    </>
  );
};

export default SessionProvider;

const useLogin = (
  publicClient: PublicClientApplication,
  dispatchEvent: (
    type: string,
    userInfo?: any,
    access_Token?: string | undefined,
  ) => void,
  setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  const { IDLE_TIMEOUT_IN_MINS } = useConfig();

  useEffect(() => {
    const doLogin = async () => {
      try {
        await publicClient.loginRedirect(loginRedirectRequest);
      } catch (err) {
        return null;
      }
    };

    let tokenRefreshTimer: any;
    const scheduleSilentRefrsh = (authTime: any) => {
      clearTimeout(tokenRefreshTimer);
      const expiresIn = Math.round(
        authTime + IDLE_TIMEOUT_IN_MINS * 60 - new Date().getTime() / 1000,
      );
      const refreshIn = (expiresIn - REFRESH_BEFORE_EXP_IN_MINS * 60) * 1000;
      tokenRefreshTimer = setTimeout(() => {
        doSilentRefresh();
      }, refreshIn);
    };

    const doSilentRefresh = async () => {
      try {
        const tokenObj = sessionStorage.getItem('GP_AUTH')
          ? JSON.parse(sessionStorage.getItem('GP_AUTH') ?? '')
          : null;
        const loginHint = tokenObj?.idTokenClaims?.email;
        const refreshTokenResponse = await publicClient
          .ssoSilent({
            ...loginRedirectRequest,
            prompt: 'none',
            loginHint,
          })
          .catch((err: Error) => {
            return null;
          });

        cleanCSRFToken();
        if (refreshTokenResponse) {
          const userInfo: any = refreshTokenResponse?.idTokenClaims;
          dispatchEvent(
            'SILENT_REFRESH',
            userInfo,
            refreshTokenResponse?.accessToken,
          );
          setIsLoggedIn(true);
          scheduleSilentRefrsh(userInfo['auth_time']);
        } else {
          doLogin();
        }
      } catch (err) {
        doLogin();
      }
    };

    const checkUserLoggedIn = async () => {
      try {
        let tokenResponse = await publicClient.handleRedirectPromise();
        let accountObj: AccountInfo | null;
        let userInfo: any = tokenResponse?.idTokenClaims;
        accountObj = tokenResponse
          ? tokenResponse.account
          : publicClient.getAllAccounts()[0];

        if (accountObj && tokenResponse) {
          dispatchEvent('SIGN_IN_SUCCESS', userInfo, tokenResponse.accessToken);
          setIsLoggedIn(true);
          scheduleSilentRefrsh(userInfo['auth_time']);
          sessionStorage.setItem('GP_AUTH', JSON.stringify(tokenResponse));
        } else if (accountObj) {
          doSilentRefresh();
        } else {
          cleanCSRFToken();
          dispatchEvent('SIGN_IN');
          setIsLoggedIn(false);
          doLogin();
        }
      } catch (err) {
        console.error('handleRedirectPromise', err);
      }
    };

    checkUserLoggedIn();
  }, [dispatchEvent, publicClient, setIsLoggedIn]);
};
